// Owns the open list on the Lists page: loading it, and every write-through edit.
//
// The save logic is a port of persistNow in the retired AdminReportBuilderPage.js:
// membership and name changes write straight through to the server, one save at a time.
// While a save is in flight the newest desired state replaces the queued one, so rapid
// adds cannot double-write or clobber each other, and the server's snapshot is only
// reconciled into the roster when nothing newer is queued (otherwise it would drop an
// athlete the user just added optimistically).
//
// Every queued save carries the id of the list it belongs to. The user can switch lists while a
// save is in flight; a save must still go to its own list, and its result must never repaint the
// pane of whichever list is open now (it once could, and the next edit then overwrote one list's
// members and name with another's).
import { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const UNTITLED = 'Untitled list';

const nameOf = (p) => p.name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'athlete';

export default function useListEditor({ onChanged } = {}) {
  const { API_URL } = useAuth();
  const cfg = useCallback(() => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  }), []);

  const [list, setList] = useState(null);          // { id, code, title, sport, members } | null
  const [canEdit, setCanEdit] = useState(false);
  const [status, setStatus] = useState('idle');    // 'idle' | 'loading' | 'saving'
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [lastAction, setLastAction] = useState(null);

  const idRef = useRef(null);
  const sportRef = useRef('');
  const titleRef = useRef('');
  const membersRef = useRef([]);                    // always the latest roster, updated synchronously
  const savingRef = useRef(false);
  const pendingRef = useRef(new Map());             // list id -> newest {id, members, title, sport} queued behind a save
  const openSeq = useRef(0);                        // latest open() wins
  const unsavedAddsRef = useRef(new Set());         // uids added to the open list since its last successful save

  const setMembers = useCallback((next) => {
    membersRef.current = next;
    setList((l) => (l ? { ...l, members: next } : l));
  }, []);

  const persist = useCallback(async (members, title, forId = idRef.current) => {
    const t = (title ?? titleRef.current).trim();
    if (!t || forId == null) return;
    const queued = { id: forId, members, title: t, sport: sportRef.current };
    if (savingRef.current) { pendingRef.current.set(forId, queued); return; }
    savingRef.current = true; setStatus('saving'); setError('');
    let job = queued;
    try {
      while (job) {
        const ids = job.members.map((m) => m.user_id);
        await axios.put(`${API_URL}/reports/${job.id}`,
          { title: job.title, sport: job.sport, athlete_user_ids: ids }, cfg());
        if (job.id === idRef.current) ids.forEach((uid) => unsavedAddsRef.current.delete(uid));
        // Re-hydrate the authoritative roster: the server drops athletes it will not list
        // (wrong sport, not an athlete account).
        const got = await axios.get(`${API_URL}/reports/${job.id}`, cfg());
        const saved = got.data.members || [];
        // Only touch the pane if this list is still the one on screen AND nothing newer is queued
        // for it (otherwise the server's snapshot would drop an athlete just added optimistically).
        if (job.id === idRef.current && !pendingRef.current.has(job.id)) {
          setMembers(saved);
          titleRef.current = got.data.title;
          setList((l) => (l ? { ...l, code: got.data.code, title: got.data.title } : l));
          const dropped = ids.length - saved.length;
          setNotice(dropped > 0
            ? `${dropped} athlete(s) weren't added — wrong sport or not an athlete account.`
            : '');
        }
        const next = pendingRef.current.entries().next();
        if (next.done) { job = null; } else { job = next.value[1]; pendingRef.current.delete(next.value[0]); }
      }
      if (onChanged) onChanged();
    } catch (e) {
      // A failed save drops the changes queued behind it (not retried; the next edit saves the whole
      // current roster again). An optimistic add that never saved flips back off the list, so the Find
      // row shows "+ Add" again and its toast goes; a failed removal keeps the row off screen.
      pendingRef.current.clear();
      const unsaved = unsavedAddsRef.current;
      if (job && job.id === idRef.current && unsaved.size) {
        setMembers(membersRef.current.filter((m) => !unsaved.has(m.user_id)));
        setLastAction((a) => (a && a.kind === 'add' && unsaved.has(a.uid) ? null : a));
      }
      unsavedAddsRef.current = new Set();
      setError(e.response?.data?.detail || 'Save failed.');
    } finally {
      savingRef.current = false; setStatus('idle');
    }
  }, [API_URL, cfg, onChanged, setMembers]);

  const open = useCallback(async (id) => {
    const seq = ++openSeq.current;
    setStatus('loading'); setError(''); setNotice(''); setLastAction(null);
    unsavedAddsRef.current = new Set();
    try {
      const res = await axios.get(`${API_URL}/reports/${id}`, cfg());
      if (seq !== openSeq.current) return;
      const d = res.data;
      idRef.current = d.id; sportRef.current = d.sport; titleRef.current = d.title;
      membersRef.current = d.members || [];
      setCanEdit(d.can_edit !== false);
      setList({ id: d.id, code: d.code, title: d.title, sport: d.sport, members: membersRef.current });
    } catch (e) {
      if (seq !== openSeq.current) return;
      idRef.current = null; membersRef.current = [];
      setList(null); setCanEdit(false);
      setError(e.response?.data?.detail || 'Failed to open list.');
    } finally {
      if (seq === openSeq.current) setStatus('idle');
    }
  }, [API_URL, cfg]);

  const createNew = useCallback(async (sport) => {
    setError(''); setNotice('');
    try {
      const res = await axios.post(`${API_URL}/reports`,
        { title: UNTITLED, sport, athlete_user_ids: [] }, cfg());
      if (onChanged) onChanged();
      await open(res.data.id);
      return res.data.id;
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not create the list.');
      return null;
    }
  }, [API_URL, cfg, onChanged, open]);

  const addMember = useCallback((p) => {
    if (!canEdit || idRef.current == null) return;
    if (membersRef.current.some((m) => m.user_id === p.user_id)) return;
    const next = [p, ...membersRef.current];
    unsavedAddsRef.current.add(p.user_id);
    setMembers(next);
    setLastAction({ kind: 'add', uid: p.user_id, name: nameOf(p), index: 0, member: p });
    persist(next);
  }, [canEdit, persist, setMembers]);

  const removeMember = useCallback((uid) => {
    if (!canEdit || idRef.current == null) return;
    const index = membersRef.current.findIndex((m) => m.user_id === uid);
    if (index < 0) return;
    const member = membersRef.current[index];
    const next = membersRef.current.filter((m) => m.user_id !== uid);
    unsavedAddsRef.current.delete(uid);
    setMembers(next);
    setLastAction({ kind: 'remove', uid, name: nameOf(member), index, member });
    persist(next);
  }, [canEdit, persist, setMembers]);

  const undo = useCallback(() => {
    if (!canEdit || !lastAction) return;
    let next;
    if (lastAction.kind === 'add') {
      next = membersRef.current.filter((m) => m.user_id !== lastAction.uid);
      unsavedAddsRef.current.delete(lastAction.uid);
    } else if (membersRef.current.some((m) => m.user_id === lastAction.uid)) {
      next = membersRef.current;            // already back (e.g. re-added by hand)
    } else {
      next = [...membersRef.current];
      next.splice(Math.min(lastAction.index, next.length), 0, lastAction.member);
    }
    setMembers(next);
    setLastAction(null);
    persist(next);
  }, [canEdit, lastAction, persist, setMembers]);

  const rename = useCallback((title) => {
    const t = (title || '').trim();
    if (!canEdit || !t || idRef.current == null || t === titleRef.current) return;
    titleRef.current = t;
    setList((l) => (l ? { ...l, title: t } : l));
    persist(membersRef.current, t);
  }, [canEdit, persist]);

  // Only while the list is empty: changing sport under existing members would orphan them.
  const changeSport = useCallback((slug) => {
    if (!canEdit || idRef.current == null || membersRef.current.length > 0 || !slug) return;
    if (slug === sportRef.current) return;
    sportRef.current = slug;
    setList((l) => (l ? { ...l, sport: slug } : l));
    persist(membersRef.current);
  }, [canEdit, persist]);

  const remove = useCallback(async () => {
    if (!canEdit || idRef.current == null) return;
    try {
      await axios.delete(`${API_URL}/reports/${idRef.current}`, cfg());
      idRef.current = null; membersRef.current = [];
      setList(null); setLastAction(null);
      if (onChanged) onChanged();
    } catch (e) {
      setError(e.response?.data?.detail || 'Delete failed.');
    }
  }, [API_URL, canEdit, cfg, onChanged]);

  // Grade (1-5) and note on one member, written through on its own (PATCH), not via the roster save.
  // Optimistic, and put back if the server refuses.
  const updateMember = useCallback(async (uid, patch) => {
    if (!canEdit || idRef.current == null) return;
    const before = membersRef.current;
    if (!before.some((m) => m.user_id === uid)) return;
    setMembers(before.map((m) => (m.user_id === uid ? { ...m, ...patch } : m)));
    try {
      await axios.patch(`${API_URL}/reports/${idRef.current}/members/${uid}`, patch, cfg());
    } catch (e) {
      setMembers(before);
      setError(e.response?.data?.detail || 'Could not save that change.');
    }
  }, [API_URL, canEdit, cfg, setMembers]);

  const dismissLastAction = useCallback(() => setLastAction(null), []);

  return {
    list, canEdit, status, notice, error, lastAction,
    open, createNew, addMember, removeMember, updateMember, undo, rename, changeSport, remove, dismissLastAction,
  };
}
